import {
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from "@mui/material/";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { DeleteIcon, EditIcon } from "../../iconImages";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import useStyles from "./Firmware.style";
import { GlobalAdmin } from "coolremote-sdk";
import ErrorBox from "../../components/WarnningBox/ErrorBox";
import EditFirmwareVersionDialog from "./EditFirmwareVersionDialog";
import {
    getReleaseNotesMarkdown,
    toReleaseNotesPayload,
} from "./firmwareReleaseNotes";
import { getFirmwareVersionId } from "../DeviceManagment/firmwareUiUtils";

const Firmware = (props: any) => {
    const styles: any = useStyles();
    const { addMessage } = useStoreActions((action) => action.errorMessage);

    const [versions, setVersions] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    const [refresh, setRefresh] = useState<boolean>(false);

    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [editVersion, setEditVersion] = useState<any>({});
    const [selectedVersionToDelete, setSelectedVersionToDelete] = useState<any>({});

    useEffect(() => {
        GlobalAdmin.getFirmwareVersions(page + 1, rowsPerPage)
            .then((res: any) => {
                const { versions = [], totalCount = 0 } = res || {};
                setVersions(versions);
                setTotalCount(totalCount);
            })
            .catch((err: any) => addMessage({ message: err.message }));
    }, [page, rowsPerPage, refresh]);

    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditVersion({});
    };

    const handleChange = (event: any) => {
        setEditVersion({ ...editVersion, [event.target.id]: event.target.value });
    };

    const handleSubmit = () => {
        const id = getFirmwareVersionId(editVersion);
        if (!id) {
            addMessage({ message: t`Firmware version id is missing` });
            return;
        }
        const { isStable, releaseNotesText } = editVersion;
        // API only accepts isRelease / isStable / isObsolete / releaseNotes
        GlobalAdmin.updateFirmwareVersion(id, {
            isStable: !!isStable,
            releaseNotes: toReleaseNotesPayload(releaseNotesText),
        })
            .then(() => setRefresh(!refresh))
            .catch((err: any) => addMessage({ message: err.message }))
            .finally(handleClose);
    };

    const openEditDialog = (firmwareVersion: any) => {
        setEditVersion({
            ...firmwareVersion,
            id: getFirmwareVersionId(firmwareVersion),
            releaseNotesText: getReleaseNotesMarkdown(firmwareVersion.releaseNotes),
        });
        setOpenDialog(true);
    };

    const deleteFirmwareVersion = () => {
        const id = getFirmwareVersionId(selectedVersionToDelete);
        if (!id) {
            addMessage({ message: t`Firmware version id is missing` });
            setSelectedVersionToDelete({});
            return;
        }
        GlobalAdmin.deleteFirmwareVersion(id)
            .then(() => setRefresh(!refresh))
            .catch((err: any) => addMessage({ message: err.message }))
            .finally(() => setSelectedVersionToDelete({}));
    };

    const renderVersion = (version: string) => {
        const dashIndex = (version || "").indexOf("-");
        if (dashIndex === -1) {
            return (
                <>
                <b>{version}</b>
            </>
            );
        }
        return (
            <>
                <b>{version.slice(0, dashIndex)}</b>
                <sup className={styles.versionSuffix}>{version.slice(dashIndex + 1)}</sup>
            </>
        );
    };

    return (
        <div className={styles.view}>
            <ServiceNavigationBar title={t`Firmware`} {...props}>
                <div className={styles.contentArea}>
                    <Grid container className={styles.content}>
                        <Grid item xs={12}>
                            <TableContainer>
                                <Table stickyHeader aria-label="firmware versions table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={styles.headCells}>{t`VERSION`}</TableCell>
                                            <TableCell className={styles.headCells}>{t`BUILD DATE`}</TableCell>
                                            <TableCell className={styles.headCells}>{t`STABLE`}</TableCell>
                                            <TableCell align="center" className={styles.headCells}>{t`ACTIONS`}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {versions.map((firmwareVersion: any) => {
                                            const id = getFirmwareVersionId(firmwareVersion);
                                            const { version, buildDate, isStable } = firmwareVersion;
                                            return (
                                                <TableRow hover tabIndex={-1} key={id || version}>
                                                    <TableCell className={styles.rowCell}>{renderVersion(version)}</TableCell>
                                                    <TableCell className={styles.rowCell}>
                                                        {buildDate ? new Date(buildDate).toLocaleDateString() : ""}
                                                    </TableCell>
                                                    <TableCell className={styles.rowCell}>{isStable ? t`Yes` : t`No`}</TableCell>
                                                    <TableCell align="center" className={styles.rowCell}>
                                                        <IconButton
                                                            onClick={() => openEditDialog(firmwareVersion)}
                                                        >
                                                            <img src={EditIcon} alt={t`Edit`} />
                                                        </IconButton>
                                                        <IconButton onClick={() => setSelectedVersionToDelete(firmwareVersion)}>
                                                            <img src={DeleteIcon} alt={t`Delete`} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                style={{ minHeight: 52 }}
                                rowsPerPageOptions={[10, 20, 50]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Grid>
                    </Grid>
                </div>

                <EditFirmwareVersionDialog
                    open={openDialog}
                    editVersion={editVersion}
                    onClose={handleClose}
                    onChange={handleChange}
                    onStableChange={(checked) =>
                        setEditVersion({ ...editVersion, isStable: checked })
                    }
                    onSubmit={handleSubmit}
                />

                {!isEmpty(selectedVersionToDelete) && (
                    <ErrorBox
                        title={t`Delete firmware version`}
                        error={t`Are you sure you want to delete firmware version ${selectedVersionToDelete.version}?`}
                        onAccept={deleteFirmwareVersion}
                        onClose={() => setSelectedVersionToDelete({})}
                    />
                )}
            </ServiceNavigationBar>
        </div>
    );
};

export default Firmware;
